// Lib
import { Text } from '@sitecore-jss/sitecore-jss-nextjs';

// Lib
import { ComponentProps } from 'lib/component-props';
import { useTheme } from 'lib/context/ThemeContext';
import { CardComponents } from 'lib/templates/Feature.Dart.model';
import { ItemEx } from 'lib/templates/_.Sitecore.Override';
import { stripHtml, stripHtmlTags } from 'lib/utils/regex';

// Local
import Button from 'helpers/Button/Button';
import ImageWrapper from 'helpers/ImageWrapper/ImageWrapper';
import LegalDisclaimer from 'helpers/LegalDisclaimer/LegalDisclaimer';
import LinkWrapper from 'helpers/LinkWrapper/LinkWrapper';
import RichTextA11yWrapper from 'helpers/RichTextA11yWrapper/RichTextA11yWrapper';
import SVG from 'helpers/SVG/SVG';
import tokensTheme from 'src/theme/token-files/brands';
import fallback from 'lib/fallback/fallback';
import { cardSVG, tailwindVariants } from './GridTailwind';
// import { Common } from 'lib/templates/Foundation.Dart.model';
import clsx from 'clsx';
import { useScreenWidth } from 'hooks/useScreenWidth';

interface ObjectStructure {
  bgcolor: string;
}
interface backgroundColor {
  backgroundColor: string;
}

export type CategoryCard = ItemEx & CardComponents.CategoryCard.CategoryCardItem;
export type CategoryCardProps = ComponentProps & CardComponents.CategoryCard.CategoryCardsList;

export type CategoryCardItem = backgroundColor &
  CardComponents.CategoryCard.CategoryCardItem & {
    id?: string;
  };

interface CardItemProps {
  cardItem: CategoryCardItem;
  withoutLink?: boolean;
  HideCardCTA?: boolean;
  showDescriptionInMobile?: boolean;
  enablePattern?: { value?: boolean };
  themeName?: string;
  grid?: Grid;
  card: () => string;
  cardLinkWrapper: () => string;
  cardMediaWrapper: () => string;
  media: () => string;
  cardSvgWrapper: () => string;
  cardContentWrapper: () => string;
  cardContentContainer: () => string;
  cardTitle: () => string;
  cardDescription: () => string;
  cardText: () => string;
  btn: () => string;
  showFullDescription?: string | undefined;
}

type Grid = '2column' | '3column' | '4column' | undefined;
type AlignCTA = 'BottomCenter' | 'TopRight' | undefined;

const CardItem: React.FC<CardItemProps> = ({
  cardItem,
  withoutLink,
  HideCardCTA,
  showDescriptionInMobile,
  enablePattern,
  themeName,
  grid,
  card,
  cardLinkWrapper,
  cardMediaWrapper,
  media,
  cardSvgWrapper,
  cardContentWrapper,
  cardContentContainer,
  cardTitle,
  cardDescription,
  cardText,
  btn,
}: // eslint-disable-next-line @typescript-eslint/no-explicit-any
any) => {
  const screenWidth = useScreenWidth();
  const isASmallScreenOrScaledView = screenWidth && screenWidth >= 996 && screenWidth < 1400;
  const CardContent = (
    <>
      <div className={cardMediaWrapper()}>
        {cardItem?.fields?.image && (
          <ImageWrapper field={cardItem?.fields?.image} layout="responsive" className={media()} />
        )}
      </div>
      <div className={cardSvgWrapper()}>
        <div
          className={clsx(
            cardContentWrapper(),
            isASmallScreenOrScaledView &&
              (grid === '3column' || grid === '4column') &&
              '!gap-1 !px-4'
          )}
        >
          <div className={cardContentContainer()}>
            <div
              className={clsx(
                cardTitle(),
                isASmallScreenOrScaledView && grid === '4column' && '!text-base !leading-normal'
              )}
            >
              <Text encode={false} field={cardItem?.fields?.title} />
            </div>
            {(showDescriptionInMobile || false) && cardItem?.fields?.description?.value && (
              <div className={cardDescription()}>
                <>
                  <RichTextA11yWrapper
                    field={cardItem?.fields?.description}
                    className={cardText({})}
                  />
                </>
              </div>
            )}
          </div>

          {!HideCardCTA && (
            <div className={btn()}>
              <Button
                title={stripHtml(cardItem?.fields?.title?.value as string)}
                type="filled"
                iconLeft="arrow_forward"
                childClass="rtl:rotate-180 !transition-none"
              />
            </div>
          )}
        </div>
        {enablePattern?.value && (
          <>
            <SVG
              svg={`CategoryCard/Breakpoint=Small,Brand=${themeName}`}
              className={`${cardSVG({
                /* eslint-disable  @typescript-eslint/ban-ts-comment */
                // @ts-ignore
                brand: themeName,
                grid: grid,
                size: 'small',
              })} ${cardItem?.backgroundColor}`}
              {...(cardItem?.fields?.bgcolor?.fields?.value?.value && {
                style: { fill: `#${cardItem?.fields?.bgcolor.fields?.value?.value}` },
              })}
            />
            <SVG
              svg={`CategoryCard/Breakpoint=Large,Brand=${themeName}`}
              className={`${cardSVG({
                /* eslint-disable  @typescript-eslint/ban-ts-comment */
                // @ts-ignore
                brand: themeName,
                grid,
                size: 'large',
              })} ${cardItem?.backgroundColor}`}
              {...(cardItem?.fields?.bgcolor?.fields?.value?.value && {
                style: { fill: `#${cardItem?.fields?.bgcolor.fields?.value?.value}` },
              })}
            />
          </>
        )}
      </div>
    </>
  );
  return (
    <div className={card()} key={cardItem?.id}>
      {!withoutLink ? (
        <LinkWrapper
          aria-label={`${stripHtml(cardItem?.fields?.title?.value as string)}${
            cardItem?.fields?.description?.value
              ? ` - ${stripHtmlTags(cardItem?.fields?.description?.value as string)}`
              : ''
          }`}
          className={cardLinkWrapper()}
          field={cardItem?.fields?.ctaLink}
          suppressLinkText
          ignoreEE
        >
          {CardContent}
        </LinkWrapper>
      ) : (
        <div className={cardLinkWrapper()}>{CardContent}</div>
      )}
    </div>
  );
};

const CategoryCardListingGrid = ({ ...props }: CategoryCardProps) => {
  const {
    title,
    description,
    primaryCTA,
    enablePattern,
    disclaimerText,
    primaryCTAType,
    primaryCTAColor,
    showDescriptionInMobile,
  } = props?.fields || {};
  const { alignCTA, grid, FieldNames, showFullDescription } = props?.params || '';
  const { componentName, dataSource } = props?.rendering || {};
  const HideCardCTA = props?.fields?.hideCTA?.value;
  const { themeName, tailwindThemeName } = useTheme();

  // Add fallback component variant color
  const fallbackComponentVariantColor = fallback?.componentVariants?.value;
  const fallbackComponentVariantType = fallback?.componentVariants?.type;

  /**
   * Filter background colors for the category card background.
   * 1. Get App name
   * 2. Get Colors
   * 3. filter out components-category-card-color-category
   */

  // Get colors for specific App name
  const variantGroup = tokensTheme?.[tailwindThemeName]?.colors;

  // Filter out background
  const filterBackground = (obj: typeof variantGroup) => {
    const result = [];

    for (const key in obj) {
      if (key.includes('components-category-card-color-category')) {
        result.push(`fill-${key}`);
      }
    }

    return result;
  };

  // Call the function
  const variants = filterBackground(variantGroup);

  // map through the key and create new array
  const result: ObjectStructure[] = variants.map((item) => ({ bgcolor: item }));

  // Combine Background Color and Category Props into a single array
  const combinedArray = props?.fields?.categoryCards?.map((category, index) => ({
    ...category,
    backgroundColor: result[index % result.length]?.bgcolor || '',
  }));

  const {
    base,
    headerContainer,
    headerTitleText,
    headerDescriptionText,
    headerTitleContainer,
    ctaContainer,
    btn,
    card,
    cardLinkWrapper,
    cardContainer,
    cardMediaWrapper,
    cardSvgWrapper,
    cardContentWrapper,
    cardContentContainer,
    media,
    cardTitle,
    cardText,
    cardDescription,
    legalDisclaimerText,
    /* eslint-disable  @typescript-eslint/ban-ts-comment */
    // @ts-ignore
  } = tailwindVariants({
    grid: grid as Grid,
    alignCTA: alignCTA as AlignCTA,
    showDescriptionInMobile: showDescriptionInMobile?.value || false,
    /* eslint-disable  @typescript-eslint/ban-ts-comment */
    // @ts-ignore
    brand: themeName as string,
    showFullDescription: showFullDescription ? 1 : undefined,
  });

  return (
    <div className={base()} data-component="authorable/categorycard/grid">
      {title?.value && (
        <div className={headerContainer()}>
          {title?.value && (
            <div className={headerTitleContainer()}>
              {title?.value && (
                <Text className={headerTitleText()} encode={false} field={title} tag="h2" />
              )}
              {description?.value && (
                <Text
                  className={headerDescriptionText()}
                  encode={false}
                  field={description}
                  tag="p"
                />
              )}
            </div>
          )}
          {primaryCTA?.value?.text && alignCTA == 'TopRight' && (
            <div className={ctaContainer()}>
              <Button
                href={primaryCTA?.value?.href}
                linktype={primaryCTA?.value?.linktype}
                label={primaryCTA?.value?.text}
                title={primaryCTA?.value?.title}
                tag="a"
                target={primaryCTA?.value?.target}
                // The design requires an outline CTA but field name is primaryCTA,
                // so for that we have added a fallback as an outline value,
                // so if there is no value in sitecore field, it will take the outline value.
                type={primaryCTAType?.value || fallbackComponentVariantType}
                color={primaryCTAColor?.value || fallbackComponentVariantColor}
                gtmEvent={{
                  event: 'cta_click',
                  type: 'primary',
                  'gtm.element.dataset.gtmLinkUrl': primaryCTA?.value?.href,
                  'gtm.element.dataset.gtmLinkName': primaryCTA?.value?.text,
                  'gtm.element.dataset.gtmDatasourceId': dataSource,
                  'gtm.element.dataset.gtmComponentName': componentName,
                }}
              />
            </div>
          )}
        </div>
      )}
      <div className={cardContainer()}>
        {combinedArray?.map((cardItem: CategoryCardItem, index: number) => (
          <CardItem
            key={index}
            cardItem={cardItem}
            withoutLink={FieldNames === 'WithoutLink'}
            HideCardCTA={HideCardCTA}
            showDescriptionInMobile
            enablePattern={enablePattern}
            themeName={themeName}
            grid={grid as Grid}
            card={card}
            cardLinkWrapper={cardLinkWrapper}
            cardMediaWrapper={cardMediaWrapper}
            media={media}
            cardSvgWrapper={cardSvgWrapper}
            cardContentWrapper={cardContentWrapper}
            cardContentContainer={cardContentContainer}
            cardTitle={cardTitle}
            cardDescription={cardDescription}
            cardText={cardText}
            btn={btn}
            showFullDescription={showFullDescription}
          />
        ))}
      </div>
      {primaryCTA?.value?.text && alignCTA == 'BottomCenter' && (
        <div className={ctaContainer()}>
          <Button
            href={primaryCTA?.value?.href}
            linktype={primaryCTA?.value?.linktype}
            label={primaryCTA?.value?.text}
            title={primaryCTA?.value?.title}
            tag="a"
            target={primaryCTA?.value?.target}
            // The design requires an outline CTA but field name is primaryCTA,
            // so for that we have added a fallback as an outline value,
            // so if there is no value in sitecore field, it will take the outline value.
            type={primaryCTAType?.value || fallbackComponentVariantType}
            color={primaryCTAColor?.value || fallbackComponentVariantColor}
            gtmEvent={{
              event: 'cta_click',
              type: 'primary',
              'gtm.element.dataset.gtmLinkUrl': primaryCTA?.value?.href,
              'gtm.element.dataset.gtmLinkName': primaryCTA?.value?.text,
              'gtm.element.dataset.gtmDatasourceId': dataSource,
              'gtm.element.dataset.gtmComponentName': componentName,
            }}
          />
        </div>
      )}
      {disclaimerText?.value != '' && (
        <LegalDisclaimer
          disclaimerText={disclaimerText}
          disclaimerClasses={legalDisclaimerText()}
        />
      )}
    </div>
  );
};

export default CategoryCardListingGrid;
